import React, { ReactNode, useState } from "react";
import { Button, Form, Dropdown, ListGroup } from "react-bootstrap";
import { useI18n } from "@/lib/i18n/i18n_provider";
import classNames from "classnames";
import { Icon } from "@/components";
import { useMediaQuery } from "@/hooks";
import { Spacer } from "@/offering/spacer";
import styles from "./capacity_filter_v2.module.css";
import { TextLink } from "@/offering/text_link";
import { useAnalytics } from "@/lib/analytics/analytics";
import { Partner } from "@/data/partner_offering";

interface ResultButtonRenderProps {
  hasError: boolean;
  localValue?: number;
  handleDone: () => void;
}
interface CapacityFilterV2Props {
  partner: Partner;
  maxCapacity: number;
  value?: number;
  onChange: (capacity: number | undefined) => void;
  onContactUs?: () => void;
  onViewEventSpaces?: () => void;
  customDropDownToggle?: ReactNode;
  customResultsButtonRender?: (props: ResultButtonRenderProps) => ReactNode;
}

export function CapacityFilterV2({
  maxCapacity,
  value,
  onChange,
  onContactUs,
  onViewEventSpaces,
  customDropDownToggle,
  customResultsButtonRender,
  partner,
}: CapacityFilterV2Props) {
  const { fbt } = useI18n();
  const { device } = useMediaQuery();
  const isMobile = device === "mobile";
  const [showDropdown, setShowDropdown] = useState(false);
  const [localValue, setLocalValue] = useState<number | undefined>(value);
  const [hasError, setHasError] = useState(
    localValue ? localValue > maxCapacity : false
  );
  const analytics = useAnalytics();

  const handleToggle = (show: boolean) => {
    setShowDropdown(show);
    if (!show && localValue !== value) {
      setLocalValue(value);
      setHasError(false);
    }
  };

  const handleIncrement = () => {
    const newValue = (localValue || 0) + 1;
    if (newValue <= maxCapacity) {
      setLocalValue(newValue);
      setHasError(false);
    }
  };

  const handleDecrement = () => {
    const newValue = (localValue || 0) - 1;
    if (newValue >= 1) {
      setLocalValue(newValue);
    } else {
      setLocalValue(undefined);
    }

    if (newValue > maxCapacity) {
      setHasError(true);
    } else {
      setHasError(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value ? Number(e.target.value) : undefined;
    if (newValue && isNaN(newValue)) {
      setLocalValue(undefined);
      setHasError(false);
      return;
    }

    setLocalValue(newValue);
    setHasError(!!newValue && newValue > maxCapacity);
  };

  const handleClearSelection = () => {
    setLocalValue(undefined);
    setHasError(false);
  };

  const handleCloseDropdown = () => {
    setShowDropdown(false);
  };

  const handleSeeResults = () => {
    if (!hasError && localValue !== value) {
      onChange(localValue);
    }
    handleCloseDropdown();

    if (localValue)
      analytics.event(
        "Capacity Filter",
        {
          "Partner UUID": partner.id,
          "Partner Name": partner.name,
          "Partner Internal Name": partner.internalName,
          Capacity: localValue,
          Source: "WL",
        },
        { onlyProviders: ["mixpanel", "customerio"] }
      );
  };

  const guestsLabel = value
    ? fbt(fbt.param("count", value) + "+ people")
    : fbt("Any number of people");

  const defaultDropDownToggle = (
    <Dropdown.Toggle className={styles.dropdownToggle}>
      {!isMobile && (
        <>
          <Icon name="people" />
          <Spacer size={8} direction="horizontal" />
          <b>{fbt("Who")}:</b>
          <Spacer size={4} direction="horizontal" />
        </>
      )}

      <div
        className={classNames(
          "d-flex align-items-center",
          isMobile ? "w-100 justify-content-between" : "g-3"
        )}
      >
        <div
          data-testid="capacity-value"
          style={{ overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {guestsLabel}
        </div>
        <Spacer size={16} direction="horizontal" />
        <div>
          <Icon name="fi-chevron-down" color="#82808E" />
        </div>
      </div>
    </Dropdown.Toggle>
  );

  return (
    <Dropdown
      className={`checkout-box-picker rounded ${styles.capacityFilterDropdown}`}
      style={{
        minHeight: "unset",
      }}
      show={showDropdown}
      onToggle={handleToggle}
      placement="bottom-start"
    >
      {customDropDownToggle ? customDropDownToggle : defaultDropDownToggle}

      <Dropdown.Menu
        style={{ width: isMobile ? "100%" : "472px", maxWidth: "472px" }}
        className="p-4"
      >
        <div>
          <span className="fw-bold pb-2">
            {fbt("Enter the minimum or expected number of people")}
          </span>
          <div className="d-flex align-items-center py-3">
            <div className={styles.inputGroup}>
              <div
                role="button"
                onClick={handleDecrement}
                aria-disabled={!localValue || localValue < 1}
                className={classNames(
                  styles.button,
                  styles.buttonLeft,
                  "d-flex align-items-center justify-content-center",
                  { [styles.buttonDisabled]: !localValue || localValue < 1 }
                )}
                aria-label={fbt("Decrease")}
              >
                -
              </div>
              <Form.Control
                type="number"
                value={localValue || ""}
                onChange={handleInputChange}
                className={classNames(styles.capacityInput, {
                  [styles.error]: hasError,
                })}
                min={1}
                placeholder={fbt("Any")}
                aria-label={fbt("Number of guests")}
              />
              <div
                role="button"
                onClick={handleIncrement}
                aria-disabled={!!localValue && localValue >= maxCapacity}
                className={classNames(
                  styles.button,
                  styles.buttonRight,
                  "d-flex align-items-center justify-content-center",
                  {
                    [styles.buttonDisabled]:
                      !!localValue && localValue >= maxCapacity,
                  }
                )}
                aria-label={fbt("Increase")}
              >
                +
              </div>
            </div>
          </div>

          {localValue && !hasError && localValue < maxCapacity ? (
            <div className="mb-3">
              <p className="mb-0">
                {fbt("We'll show rooms that fit")}{" "}
                <strong>
                  {fbt(fbt.param("count", localValue) + "+ guests")}{" "}
                </strong>{" "}
                {fbt("and more spacious options if available.")}
              </p>
            </div>
          ) : null}

          {localValue && localValue >= maxCapacity && (
            <div className="mb-3">
              <p className="mb-0">
                {fbt(
                  "Our meeting rooms fit up to " +
                    fbt.param("max", maxCapacity) +
                    " guests."
                )}{" "}
                {onViewEventSpaces ? (
                  <>
                    {" "}
                    {fbt("For larger groups, explore our")}{" "}
                    <TextLink onPress={onViewEventSpaces}>
                      {fbt("event spaces")}{" "}
                    </TextLink>
                    {fbt("or")}{" "}
                    {onContactUs ? (
                      <TextLink onPress={onContactUs}>
                        {fbt("contact us")}.
                      </TextLink>
                    ) : (
                      <TextLink href="mailto:help@flexspace.ai">
                        {fbt("contact us")}.
                      </TextLink>
                    )}
                  </>
                ) : (
                  <>
                    {onContactUs ? (
                      <TextLink onPress={onContactUs}>
                        {" " + fbt("Contact us")}
                      </TextLink>
                    ) : (
                      <TextLink href="mailto:help@flexspace.ai">
                        {" " + fbt("Contact us")}
                      </TextLink>
                    )}
                    {" " + fbt("for larger groups") + " "}
                  </>
                )}
              </p>
            </div>
          )}
        </div>

        <div className={styles.dropdownFooter}>
          <div
            role="button"
            className={classNames(styles.clearButton, {
              [styles.active]: localValue,
            })}
            onClick={handleClearSelection}
          >
            {fbt("Clear selection")}
          </div>
          {customResultsButtonRender ? (
            customResultsButtonRender({
              hasError,
              localValue,
              handleDone: handleSeeResults,
            })
          ) : (
            <Button
              disabled={hasError || localValue === value}
              onClick={handleSeeResults}
            >
              {fbt("See results")}
            </Button>
          )}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}

export const ModalCapacityFilterItem = ({
  maxCapacity,
  value,
  onChange,
  onContactUs,
  onViewEventSpaces,
  partner,
}: Omit<CapacityFilterV2Props, "customDropDownToggle">) => {
  const { fbt } = useI18n();
  const guestsLabel = value
    ? fbt(fbt.param("count", value) + "+ people")
    : fbt("Any number of people");

  return (
    <ListGroup.Item
      className={`${styles.mobileCapacityFilterItem} mobile-filter-item`}
    >
      <CapacityFilterV2
        maxCapacity={maxCapacity}
        value={value}
        onChange={onChange}
        onContactUs={onContactUs}
        onViewEventSpaces={onViewEventSpaces}
        partner={partner}
        customDropDownToggle={
          <Dropdown.Toggle className={styles.modalDropdownToggle}>
            <div className={styles.displayValue}>
              <div className="fw-bold text-size-xs">{fbt("Who")}</div>
              <div className={value ? undefined : styles.label}>
                {guestsLabel}
              </div>
            </div>

            <div>
              <Icon name="fi-chevron-down" color="#82808E" />
            </div>
          </Dropdown.Toggle>
        }
        customResultsButtonRender={({ hasError, localValue, handleDone }) => {
          return (
            <Button
              disabled={hasError || localValue === value}
              onClick={handleDone}
            >
              {fbt("Done")}
            </Button>
          );
        }}
      />
    </ListGroup.Item>
  );
};
